/** @file rotateHandles: the rotate tool's meshes (three rings in near and far halves, the outer ring). */
import { Quaternion } from '@babylonjs/core/Maths/math.vector.js';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';
import type { Scene } from '@babylonjs/core/scene.js';
import { axesLocal } from './axesLocal';
import type { GizmoFrame, HandleSet } from './gizmoFrame';
import { gizmoLine, type GizmoLine } from './gizmoLine';
import { GIZMO_METRICS as M } from './gizmoMetrics';
import type { GizmoNodes } from './gizmoNodes';
import { GIZMO_PALETTE } from './gizmoPalette';
import { handlePaint } from './handlePaint';
import { lineWidth } from './lineWidth';
import { ringFacing } from './ringFacing';
import { ringTurn } from './ringTurn';
import { unitRing } from './unitRing';
import { yawMarks } from './yawMarks';

/** Above this face-on measure a ring's far half is drawn opaque (seen square on, nothing is behind). */
const FACE_ON = 0.9;

/** One axis ring: a pivot turned about the axis each frame, its near half and its far half. */
type Ring = { k: 0 | 1 | 2; pivot: TransformNode; near: GizmoLine; far: GizmoLine };

/** Ring `k`'s two halves (angles `-pi/2..pi/2` and `pi/2..3pi/2` before the pivot turns them) under a pivot. */
function ring(scene: Scene, nodes: GizmoNodes, k: 0 | 1 | 2): Ring {
  const pivot = new TransformNode(`viewport-gizmo-ring-${String(k)}`, scene);
  pivot.rotationQuaternion = new Quaternion();
  pivot.parent = nodes.frame;
  const u = (k + 1) % 3;
  const v = (k + 2) % 3;
  const c = GIZMO_PALETTE.handle[k] ?? GIZMO_PALETTE.hover;
  const half = (from: number, name: string): GizmoLine =>
    gizmoLine(`viewport-gizmo-ring-${String(k)}-${name}`, unitRing(u, v, 1, from, from + Math.PI).map(axesLocal), pivot, scene, c);
  return { k, pivot, near: half(-Math.PI / 2, 'near'), far: half(Math.PI / 2, 'far') };
}

/**
 * Rings X, Y, Z on the axes frame: each frame the pivot turns the near half to face the viewer (`ringFacing`) and the
 * far half is drawn at `farAlpha` (opaque when the ring is seen face-on); the white silhouette circle of the same
 * radius (free rotate, `outerWidth` wide at `outerAlpha`) on the screen node: the trackball's outline.
 */
export function rotateHandles(scene: Scene, nodes: GizmoNodes): HandleSet {
  const rings = ([0, 1, 2] as const).map((k) => ring(scene, nodes, k));
  const outer = gizmoLine('viewport-gizmo-ring-outer', unitRing(0, 1, 1), nodes.silhouette, scene, GIZMO_PALETTE.silhouette);
  const yaw = yawMarks(scene, nodes);
  const lines = [...rings.flatMap((r) => [r.near, r.far]), outer, ...yaw.lines];
  const facing: [number, number] = [0, 0];
  return {
    lines,
    setEnabled: (on) => { for (const l of lines) l.mesh.setEnabled(on); },
    update: (f: GizmoFrame) => {
      yaw.update(f);
      for (const r of rings) {
        const on = (f.show & (1 << r.k)) !== 0;
        r.near.mesh.setEnabled(on);
        r.far.mesh.setEnabled(on);
        ringFacing(f.model, f.view, r.k, facing);
        if (r.pivot.rotationQuaternion !== null) ringTurn(r.k, facing[0], r.pivot.rotationQuaternion);
        const w = lineWidth(f, r.k);
        r.near.style(handlePaint(f, r.k), w);
        r.far.style(handlePaint(f, r.k), w, facing[1] < FACE_ON ? M.farAlpha : 1);
      }
      outer.mesh.setEnabled((f.show & 8) !== 0);
      outer.style(f.hover === 3 ? GIZMO_PALETTE.hover : GIZMO_PALETTE.silhouette, lineWidth(f, 3, M.outerWidth), f.hover === 3 ? 1 : M.outerAlpha);
    },
  };
}
